import type { RoomRateRatePlanOption } from '../RoomRatesStep/RoomRateRoomRow';
import type { AppliedFlashRoomRate } from '../types';

/** The rate plans applied to each room, in the order they were applied, one entry per plan. */
export function getRatePlanOptionsByRoomKey(appliedFlashRoomRates: Array<AppliedFlashRoomRate>) {
  const optionsByRoomKey: Record<string, Array<RoomRateRatePlanOption>> = {};

  appliedFlashRoomRates.forEach(({ ratePlanId, ratePlanName, roomTypeId }) => {
    const options = optionsByRoomKey[roomTypeId] ?? [];

    if (!options.some((option) => option.id === ratePlanId)) {
      optionsByRoomKey[roomTypeId] = options.concat({ id: ratePlanId, name: ratePlanName || ratePlanId });
    }
  });

  return optionsByRoomKey;
}

export type PackageRatePlanSource = 'ai' | 'manual' | 'only';

// The rate plan each package uses: the CM's pick, else the AI's, else the room's only one, each only while the room
// still offers it. A package with several to choose from and no valid pick is omitted, so it can't link.
export function resolvePackageRatePlans({
  overrides,
  packageRoomKeys,
  ratePlanOptionsByRoomKey,
  suggestions = {},
}: {
  overrides: Record<string, string>;
  packageRoomKeys: Record<string, string>;
  ratePlanOptionsByRoomKey: Record<string, Array<RoomRateRatePlanOption>>;
  suggestions?: Record<string, string>;
}) {
  const resolved: Record<string, { ratePlanId: string; source: PackageRatePlanSource }> = {};

  Object.entries(packageRoomKeys).forEach(([packageId, roomKey]) => {
    const options = ratePlanOptionsByRoomKey[roomKey] ?? [];
    const isOffered = (ratePlanId: string | undefined): ratePlanId is string =>
      !!ratePlanId && options.some((option) => option.id === ratePlanId);

    if (isOffered(overrides[packageId])) {
      resolved[packageId] = { ratePlanId: overrides[packageId], source: 'manual' };
    } else if (isOffered(suggestions[packageId])) {
      resolved[packageId] = { ratePlanId: suggestions[packageId], source: 'ai' };
    } else if (options.length === 1) {
      resolved[packageId] = { ratePlanId: options[0].id, source: 'only' };
    }
  });

  return resolved;
}
